import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import { Download, ArrowRight, Clock, CreditCard } from "lucide-react";

/**
 * Customer-facing dashboard.
 *  • Lists the user's audits (most recent first).
 *  • For the free auto-snapshot — once status === "complete", reveal the
 *    "View snapshot" + "Download PDF" actions.
 *  • For paid follow-up audits queued for admin review — show a friendly
 *    "Pending review" state with a note about payment.
 */
export default function MySnapshot() {
  const [audits, setAudits] = useState(null);
  const [error, setError] = useState("");
  const [paymentBanner, setPaymentBanner] = useState(null); // {message, tone}
  const location = useLocation();
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/audits");
      setAudits(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to load your snapshots.");
    }
  }, []);

  // Initial load + light polling for status changes
  useEffect(() => {
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [load]);

  // After Stripe redirects back with ?session_id=..., poll for paid status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      setPaymentBanner({ tone: "warn", message: "Payment cancelled. You can retry whenever you're ready." });
      navigate("/my-snapshot", { replace: true });
      return;
    }
    if (!sid) return;
    setPaymentBanner({ tone: "pending", message: "Confirming your payment with Stripe…" });

    let attempts = 0;
    const maxAttempts = 10;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setPaymentBanner({ tone: "success", message: "Payment received — your Full Audit is running. We'll email you when it's ready." });
          load();
          navigate("/my-snapshot", { replace: true });
          return;
        }
        if (data.status === "expired") {
          setPaymentBanner({ tone: "warn", message: "Payment session expired. Please retry." });
          navigate("/my-snapshot", { replace: true });
          return;
        }
        if (attempts >= maxAttempts) {
          setPaymentBanner({ tone: "warn", message: "Still confirming — refresh in a moment, or check your email." });
          return;
        }
        pollRef.current = setTimeout(poll, 2000);
      } catch (e) {
        setPaymentBanner({ tone: "warn", message: "Couldn't check payment status. Please refresh." });
      }
    };
    poll();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16" data-testid="my-snapshot-page">
      <Helmet>
        <title>My Snapshot · The Entity Agency</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#f4efe6] leading-tight" style={{ fontWeight: 600 }}>
        Your AI Visibility Snapshot
      </h1>
      <p className="mt-3 text-[#c9bca4] max-w-2xl">
        Your free snapshot generates automatically. Additional audits are reviewed by The Entity Agency team before delivery.
      </p>

      {paymentBanner && (
        <div
          data-testid="payment-banner"
          className="mt-6 px-4 py-3 rounded-sm text-sm font-mono-eav tracking-wider"
          style={{
            background:
              paymentBanner.tone === "success" ? "rgba(109,182,109,0.10)"
              : paymentBanner.tone === "warn" ? "rgba(196,106,90,0.10)"
              : "rgba(184,150,62,0.10)",
            color:
              paymentBanner.tone === "success" ? "#6db66d"
              : paymentBanner.tone === "warn" ? "#c46a5a"
              : "#b8963e",
            border: `1px solid ${
              paymentBanner.tone === "success" ? "rgba(109,182,109,0.35)"
              : paymentBanner.tone === "warn" ? "rgba(196,106,90,0.35)"
              : "rgba(184,150,62,0.35)"
            }`,
          }}
        >
          {paymentBanner.message}
        </div>
      )}

      {error && (
        <div data-testid="my-snapshot-error" className="mt-6 text-sm text-[#c46a5a] bg-[#c46a5a]/10 border border-[#c46a5a]/30 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {audits === null && (
        <div className="mt-10 text-sm font-mono-eav text-[#8a7e6d] tracking-wider">LOADING…</div>
      )}

      {audits !== null && audits.length === 0 && (
        <div className="mt-12 eav-card p-10 text-center">
          <p className="text-[#c9bca4]">You haven't run a snapshot yet.</p>
          <Link to="/audit/new" data-testid="my-snapshot-empty-cta" className="btn-gold mt-6 inline-flex">
            Run my free snapshot <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {audits !== null && audits.length > 0 && (
        <div className="mt-10 space-y-4">
          {audits.map((a) => (
            <React.Fragment key={a.id}>
              <AuditCard audit={a} />
              {a.status === "complete" && a.audit_kind === "snapshot" && !audits.some(
                (x) => x.audit_kind === "full" && x.parent_snapshot_id === a.id,
              ) && (
                <UpgradeToFullCard snapshotId={a.id} companyName={a.company_name} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="mt-12 pt-8 border-t border-[#2a2620] flex flex-wrap items-center gap-3 justify-between">
        <p className="text-sm text-[#8a7e6d]">
          Need a deeper audit? Reply to your snapshot email or contact us at
          <span className="text-[#b8963e]"> contact@theentityagency.com</span>.
        </p>
        <Link to="/audit/new" data-testid="my-snapshot-run-again" className="btn-ghost text-sm">
          Request another audit
        </Link>
      </div>
    </div>
  );
}

function AuditCard({ audit }) {
  const isComplete = audit.status === "complete";
  const isPending  = audit.status === "pending_review";
  const isWorking  = ["queued", "running"].includes(audit.status);
  const isFailed   = audit.status === "failed";

  const statusLabel = {
    complete:        "Ready",
    queued:          "Queued",
    running:         "Generating",
    pending_review:  "Pending admin review",
    awaiting_input:  "Awaiting input",
    ready_for_scoring: "Awaiting score",
    failed:          "Failed",
  }[audit.status] || audit.status;

  const statusDot = {
    complete:       "#6db66d",
    queued:         "#d4a04a",
    running:        "#d4a04a",
    pending_review: "#b8963e",
    failed:         "#c46a5a",
  }[audit.status] || "#8a7e6d";

  return (
    <div
      data-testid={`my-snapshot-card-${audit.id}`}
      className="eav-card p-6 flex flex-wrap items-center gap-4 justify-between"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusDot }} />
          <span className="font-mono-eav text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: statusDot }}>
            {statusLabel}
          </span>
          {audit.is_free && (
            <span className="font-mono-eav text-[9px] tracking-[0.2em] uppercase text-[#5e5448] border border-[#3a352c] px-1.5 py-0.5 rounded-sm ml-1">
              FREE
            </span>
          )}
        </div>
        <p className="mt-2 font-serif-display text-2xl text-[#f4efe6]" style={{ fontWeight: 600 }}>
          {audit.company_name}
        </p>
        <p className="mt-1 text-sm text-[#8a7e6d]">
          {audit.audit_kind === "full" && (
            <span className="font-mono-eav text-[10px] tracking-[0.2em] uppercase text-[#b8963e] mr-1">
              FULL AUDIT ·
            </span>
          )}
          {audit.industry} · {audit.region}
          {audit.total_score != null && (
            <>
              {" "}· <span className="text-[#b8963e]">{audit.total_score}/100 · {audit.tier_label}</span>
            </>
          )}
        </p>
      </div>

      <div className="flex items-center gap-2">
        {isComplete && (
          <>
            <Link
              to={`/snapshot/${audit.id}`}
              data-testid={`my-snapshot-view-${audit.id}`}
              className="btn-ghost text-sm"
            >
              View report
            </Link>
            {/* Full Audit PDF gated behind admin approval per spec; backward-compat: delivered audits stay unlocked */}
            {(audit.audit_kind !== "full" || audit.admin_approved_at || audit.delivered_to_customer_at) ? (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/audits/${audit.id}/pdf`}
                data-testid={`my-snapshot-pdf-${audit.id}`}
                className="btn-gold text-sm"
                target="_blank"
                rel="noreferrer"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
            ) : (
              <span
                data-testid={`my-snapshot-pdf-pending-${audit.id}`}
                className="font-mono-eav text-[10px] tracking-[0.2em] uppercase text-[#b8963e] border border-[#b8963e]/40 px-2 py-1 rounded-sm"
                title="Your Full Audit is pending final review by The Entity Agency team."
              >
                PDF · awaiting agency review
              </span>
            )}
          </>
        )}
        {isWorking && (
          <div className="flex items-center gap-2 text-[#d4a04a] font-mono-eav text-xs tracking-wider">
            <Clock className="w-4 h-4 animate-pulse" /> WORKING…
          </div>
        )}
        {isPending && (
          <PayButton auditId={audit.id} auditKind={audit.audit_kind} />
        )}
        {isFailed && (
          <span className="font-mono-eav text-xs text-[#c46a5a] tracking-wider">FAILED · CONTACT US</span>
        )}
      </div>
    </div>
  );
}

function PayButton({ auditId, auditKind }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const isFullAudit = auditKind === "full";
  const priceLabel = isFullAudit ? "Pay $300 to unlock Full Audit" : "Pay $5 to unlock";

  const startCheckout = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/payments/checkout", {
        audit_id: auditId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to start checkout.");
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">
        {isFullAudit ? "Awaiting payment" : "Awaiting review & payment"}
      </div>
      <button
        type="button"
        onClick={startCheckout}
        disabled={loading}
        data-testid={`my-snapshot-pay-${auditId}`}
        className="btn-gold text-sm"
      >
        <CreditCard className="w-4 h-4" />
        {loading ? "Opening Stripe…" : priceLabel}
      </button>
      {err && (
        <div className="text-xs text-[#c46a5a] max-w-xs text-right">{err}</div>
      )}
    </div>
  );
}


/**
 * Upsell card shown directly under a completed snapshot row.
 * Spec: "Upgrade to Full AI Visibility Audit — $300". Flow:
 *   1. POST /api/audits/{snapshot_id}/upgrade-to-full → mints a pending_review full audit
 *      that inherits the snapshot baseline.
 *   2. POST /api/payments/checkout → Stripe Checkout URL.
 *   3. Redirect to Stripe; on return /my-snapshot?session_id=… polls payment status.
 */
function UpgradeToFullCard({ snapshotId, companyName }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const startUpgrade = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: upgrade } = await api.post(`/audits/${snapshotId}/upgrade-to-full`);
      if (!upgrade.requires_payment) {
        window.location.href = "/my-snapshot";
        return;
      }
      const { data: checkout } = await api.post("/payments/checkout", {
        audit_id: upgrade.audit_id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to start the upgrade. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`upgrade-to-full-${snapshotId}`}
      className="eav-card p-6 border border-[#b8963e]/40 bg-gradient-to-br from-[#0c0a08] to-[#080807]"
    >
      <div className="flex flex-wrap items-start gap-6 justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono-eav text-[10px] tracking-[0.22em] uppercase font-bold text-[#b8963e]">
              Available upgrade
            </span>
          </div>
          <h3
            className="mt-2 font-serif-display text-2xl text-[#f4efe6] leading-tight"
            style={{ fontWeight: 600 }}
          >
            Upgrade {companyName} to a <em className="italic text-[#b8963e]">Full AI Visibility Audit</em>
          </h3>
          <p className="mt-2 text-sm text-[#c9bca4] max-w-2xl">
            A 25–40 page deliverable: deeper platform testing across 7 prompt groups, hallucination
            evidence, competitive intelligence, AI search journey map, revenue impact, and a 90-day
            roadmap. Delivered within 48 hours of approval.
          </p>
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">
            One-time
          </div>
          <div className="font-serif-display text-3xl text-[#b8963e]" style={{ fontWeight: 700 }}>
            $300
          </div>
          <button
            type="button"
            onClick={startUpgrade}
            disabled={loading}
            data-testid={`upgrade-to-full-cta-${snapshotId}`}
            className="btn-gold text-sm mt-2"
          >
            <CreditCard className="w-4 h-4" />
            {loading ? "Opening Stripe…" : "Start Full Audit"}
          </button>
        </div>
      </div>
      {err && (
        <div className="mt-3 text-xs text-[#c46a5a] bg-[#c46a5a]/10 border border-[#c46a5a]/30 px-3 py-2 rounded-sm">
          {err}
        </div>
      )}
    </div>
  );
}
